var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { SeriesModel } from '../../model';
import { Group, CompoundPath, Line, Polyline } from '../../element';
import * as lodash from '../../utils/lodash';
var StreamGraph = /** @class */ (function (_super) {
    __extends(StreamGraph, _super);
    function StreamGraph() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'streamgraph';
        _this.hideLegendItems = new Set();
        return _this;
    }
    StreamGraph.prototype.getDefaultOption = function () {
        return {
            type: 'streamgraph',
            alias: {},
            name: '',
            coord: 'cartesian2d',
            polar: null,
            xAxis: 0,
            yAxis: 0,
            encode: {
                x: 'x',
                y: ['y'],
            },
            shapeStyle: {
                lineWidth: 1,
            },
            hover: {
                shapeStyle: {
                    stroke: '#1A1E22',
                    lineWidth: 1,
                },
            },
            label: null,
            showLegend: true,
            legendType: 'custom',
            visible: true,
        };
    };
    StreamGraph.prototype.getInitialState = function () {
        return {
            points: [],
            needAnimation: true,
        };
    };
    StreamGraph.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.option, _b = _a.encode, xField = _b.x, valueFields = _b.y, alias = _a.alias;
        var coord = this.getCoord();
        var xScale = coord.getXScale();
        var yScale = coord.getYScale();
        var colors = this.chart.getOption().colors;
        this.state.points = this._dataset.mapData(function (record, dataIndex) {
            var x = xScale.scale(dataIndex);
            var name = record[xField];
            var subPoints = [];
            var pointMax = valueFields.reduce(function (prev, yField, fieldIndex) {
                var color = colors[fieldIndex % colors.length];
                var value = Number(record[yField]);
                var visible = !_this.hideLegendItems.has(yField);
                var sum = prev;
                if (!isNaN(value) && visible) {
                    sum += value;
                }
                subPoints.push({
                    range: [prev, sum],
                    plotX: x,
                    plotY: 0,
                    stackX: x,
                    stackY: 0,
                    name: alias[yField] || yField,
                    value: value,
                    color: color,
                    visible: visible,
                });
                return sum;
            }, 0);
            var offset = (yScale.min + yScale.max) / 2 - pointMax / 2;
            subPoints.forEach(function (point) {
                var _a = point.range, y1 = _a[0], y2 = _a[1];
                point.plotY = yScale.scale(y2 + offset);
                point.stackY = yScale.scale(y1 + offset);
            });
            return {
                name: name,
                items: subPoints,
                plotX: 0,
                plotY: 0,
                encode: null,
                value: null,
                visible: true,
                color: null,
            };
        });
    };
    StreamGraph.prototype.togglePointVisible = function (dataIndex, visible) {
        var field = this.option.encode.y[dataIndex];
        visible ? this.hideLegendItems.delete(field) : this.hideLegendItems.add(field);
    };
    StreamGraph.prototype.getLegendItems = function () {
        var _this = this;
        var colors = this.chart.getOption().colors;
        return this.option.encode.y.map(function (field, index) {
            var color = colors[index % colors.length];
            return {
                name: field,
                value: null,
                color: color,
                visible: !_this.hideLegendItems.has(field),
                legendType: 'custom',
                legendIcon: undefined,
                seriesIndex: _this.seriesIndex,
                dataIndex: index,
                pageIndex: 0,
            };
        });
    };
    StreamGraph.prototype.render = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('streamgraph'),
        });
        this.option.encode.y.forEach(function (field, index) {
            var points = _this.state.points.map(function (point) { return point.items[index]; });
            if (!points.length) {
                return;
            }
            var colors = _this.chart.getOption().colors;
            var color = colors[index % colors.length];
            var lineStart = new Line({
                x1: points[0].stackX,
                y1: points[0].stackY,
                x2: points[0].plotX,
                y2: points[0].plotY,
            });
            var lastPoint = lodash.last(points);
            var lineEnd = new Line({
                x1: lastPoint.plotX,
                y1: lastPoint.plotY,
                x2: lastPoint.stackX,
                y2: lastPoint.stackY,
            });
            var polyline = new Polyline({
                smooth: true,
                smoothMonotone: 'x',
                pointList: points.map(function (point) { return ({ x: point.plotX, y: point.plotY }); }),
            });
            var stackLine = new Polyline({
                smooth: true,
                smoothMonotone: 'x',
                pointList: points.map(function (point) { return ({ x: point.stackX, y: point.stackY }); }).reverse(),
            });
            group.add(new CompoundPath(__assign(__assign({ key: index, shapes: [lineStart, polyline, lineEnd, stackLine], fill: color, stroke: color, cursor: 'pointer' }, _this.option.shapeStyle), { lineWidth: 1, onMouseEnter: function (e) {
                    e.target.parentNode.add(e.target);
                    e.target.setAttr(__assign(__assign({}, _this.option.shapeStyle), _this.option.hover.shapeStyle));
                }, onMouseLeave: function (e) {
                    e.target.setAttr(__assign({ stroke: color }, _this.option.shapeStyle));
                } })));
        });
        return group;
    };
    StreamGraph.dependencies = ['coord', 'legend'];
    return StreamGraph;
}(SeriesModel));
export default StreamGraph;
